// Per-type detection state machine shared by Multiple-Person and
// Person-Switch detection. Each caller creates its OWN independent instance
// (no shared state between instances) — only the escalation COUNT is shared
// across types, via violationStrikeCounter.js, which each caller consults
// after an "escalate" result.
//
//   idle -> confirming (short window, e.g. 2s)
//        -> confirmed: log the violation now (evidence posted to backend),
//           no popup yet -> monitoring begins
//   monitoring (longer window, e.g. 10s), re-checked repeatedly
//        -> resolved: violation cleared before the window elapsed -> back to
//           idle, ready for a fresh episode, nothing further happens
//        -> escalate: STILL violating once the window elapsed -> caller
//           registers a strike and shows the popup (or terminates, per the
//           shared counter). This fires again every time a FURTHER window
//           still finds it violating -- a sustained, never-resolving
//           episode keeps escalating (warn, warn, terminate) entirely on
//           its own, exactly like the original spec requires. It is the
//           shared strike counter (violationStrikeCounter.js), not this
//           engine, that caps it at 2 warnings before terminating.
//
// A window's fate is decided exactly once, at its own checkpoint, from the
// ratio of violating samples across the FULL window — never from a single
// sample. That is what makes it resilient to transient detection flips
// without needing to pause or restart the workflow.
export function createEscalationEngine({
  confirmationWindowMs = 2000,
  monitorWindowMs = 10000,
  violationRatioThreshold = 0.6,
  maxWindowSamples = 50,
} = {}) {
  let phase = "idle"; // "idle" | "confirming" | "monitoring"
  let windowStart = null;
  let windowSamples = [];
  let pausedAt = null;

  function ratioOf(samples) {
    if (!samples.length) return 0;
    return samples.filter((s) => s.violating).length / samples.length;
  }

  function buildEvidence(samples, ratio, windowDurationMs) {
    return {
      ratio: Number(ratio.toFixed(2)),
      sampleCount: samples.length,
      windowDurationMs,
      generatedAt: new Date().toISOString(),
      history: samples.map(({ time, violating, ...rest }) => ({
        t: new Date(time).toISOString(),
        violating,
        ...rest,
      })),
    };
  }

  function reset() {
    phase = "idle";
    windowStart = null;
    windowSamples = [];
    pausedAt = null;
  }

  // Suspends the window's clock (e.g. the caller is skipping samples for a
  // known-legitimate reason, like Paper/Pen mode) WITHOUT discarding an
  // already-confirmed "monitoring" episode -- calling reset() here instead
  // would let a genuine, already-logged identity mismatch dodge its strike
  // just by having Paper/Pen mode toggled during the monitoring window.
  function pause(now = Date.now()) {
    if (phase === "idle" || pausedAt !== null) return;
    pausedAt = now;
  }

  // Shifts windowStart forward by however long was paused, so the paused
  // interval doesn't count against confirmationWindowMs/monitorWindowMs.
  function resume(now = Date.now()) {
    if (pausedAt === null) return;
    if (windowStart !== null) windowStart += now - pausedAt;
    pausedAt = null;
  }

  /**
   * Call once per detection cycle with whether this sample is violating
   * (mismatched identity / more than one face / etc) plus any extra
   * evidence fields to preserve (distance, confidence score, face count...).
   * Returns { action }:
   *   "none"      -- nothing to do, keep sampling
   *   "confirmed" -- the initial (short) confirmation window elapsed and the
   *                  violation held; caller should log ONE violation with
   *                  `evidence`, no popup yet
   *   "escalate"  -- the (longer) monitoring window elapsed and the SAME
   *                  episode is still violating; caller should register a
   *                  strike with the shared counter and show the popup (or
   *                  terminate, per the counter's decision) -- fires again
   *                  on every further window the episode remains violating
   *   "resolved"  -- the episode cleared during monitoring; caller should
   *                  dismiss any popup shown for it
   */
  function evaluate(now, violating, meta = {}) {
    const sample = { time: now, violating, ...meta };

    if (phase === "idle") {
      if (!violating) return { action: "none" };
      phase = "confirming";
      windowStart = now;
      windowSamples = [sample];
      return { action: "none" };
    }

    windowSamples.push(sample);
    if (windowSamples.length > maxWindowSamples) windowSamples.shift();

    const duration =
      phase === "confirming" ? confirmationWindowMs : monitorWindowMs;
    const elapsed = now - windowStart;
    if (elapsed < duration) return { action: "none" };

    const ratio = ratioOf(windowSamples);
    const stillViolating = ratio >= violationRatioThreshold;

    if (phase === "confirming") {
      if (!stillViolating) {
        reset(); // was noise, never actually confirmed
        return { action: "none" };
      }
      const evidence = buildEvidence(windowSamples, ratio, elapsed);
      phase = "monitoring";
      windowStart = now;
      windowSamples = [];
      return { action: "confirmed", evidence };
    }

    // phase === "monitoring" -- deciding whether this SAME episode resolved
    if (!stillViolating) {
      reset();
      return { action: "resolved" };
    }

    const evidence = buildEvidence(windowSamples, ratio, elapsed);
    windowStart = now;
    windowSamples = [];
    return { action: "escalate", evidence };
  }

  return { evaluate, reset, pause, resume };
}
